import store from 'store'
import queryString from 'query-string'
import * as Sentry from '@sentry/react'
import { store as reduxStore } from 'index'
import { SERVER_NAME } from 'conf.js'
import packageInfo from '../../../package.json'
import { updateToken, pollForRefreshTokenChange, RESCUE_POLL_TIMEOUT_MS } from '../jwt'
import { captureForcedLogout } from '../sentryLog'
import { updateServerTimeOffset } from '../totp/timeSync'

export const captureSentryError = (error, extra) => {
  if (typeof Sentry.captureException !== 'function') return
  const errObj = error instanceof Error ? error : new Error(String(error))
  const { area, ...rest } = extra || {}
  Sentry.captureException(errObj, {
    level: 'error',
    tags: area ? { area } : undefined,
    extra: rest,
  })
}

function RestClient(apiURL, fetchFunc, tokenKey, getAuthFunc, workspaceIdKey) {
  const self = this

  self.apiURL = apiURL

  self.fetch = fetchFunc

  const HEADER_APP = 'x-ni-sb-app'
  const HEADER_APP_VERSION = 'x-ni-sb-app-version'

  const getWorkspaceId = () => store.get(workspaceIdKey)

  const IS_GLOBAL =
    (process.env.REACT_APP_SERVER === SERVER_NAME.JAPAN &&
      process.env.REACT_APP_TARGET === 'production') ||
    process.env.REACT_APP_TARGET === SERVER_NAME.JAPAN_DEV ||
    process.env.REACT_APP_TARGET === SERVER_NAME.JAPAN_STAGING

  const isSafboxWorkspace = () => {
    const wsId = getWorkspaceId()
    return wsId != null && wsId !== undefined && IS_GLOBAL
  }

  const applyTokenForOptions = (options, url) => {
    if (options.headers == null) {
      options.headers = {}
    }

    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      options.headers[HEADER_APP] = 'Web App'
      options.headers[HEADER_APP_VERSION] = packageInfo.version

      if (getWorkspaceId() != null) {
        options.headers['workspace-id'] = getWorkspaceId()
      }
    }

    if (tokenKey != null) {
      const token = store.get(tokenKey)
      if (token != null) {
        if (getAuthFunc == null) {
          options.headers.authorization = `JWT ${token}`
        } else {
          const { key, value } = getAuthFunc(token)
          options.headers[key] = value
        }
      }
    }
  }

  const applyTokenForXHR = (xhr, url) => {
    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      xhr.setRequestHeader(HEADER_APP, 'Web App')
      xhr.setRequestHeader(HEADER_APP_VERSION, packageInfo.version)

      // workspace-id 헤더 추가
      if (getWorkspaceId() != null) {
        xhr.setRequestHeader('workspace-id', getWorkspaceId())
      }
    }

    if (tokenKey != null) {
      const token = store.get(tokenKey)
      if (token != null) {
        if (getAuthFunc == null) {
          xhr.setRequestHeader('authorization', `JWT ${token}`)
        } else {
          const { key, value } = getAuthFunc(token)
          xhr.setRequestHeader(key, value)
        }
      }
    }
  }

  let isErrorShown = true
  // retryFn 은 같은 요청을 동일 옵션으로 다시 트리거하는 closure.
  // 401/509/403 등 race-induced staleness 와 구별 안 되는 응답에서
  // 즉시 LOGOUT 하기 전 익스텐션이 토큰을 회전시킨 경우 (= refresh token
  // 값이 polling 도중 바뀌는 경우) 만 새 토큰으로 1회 재시도. 정말 만료면
  // 3s 후 동일하게 LOGOUT. retryFn=null 이면 재귀 1회 제한 (무한 루프 방지).
  const processResponse = (reqPromise, retryFn) => {
    return new Promise((resolve, reject) => {
      // customFetchWithToken은 토큰이 비었거나 세션이 만료되면 동기 null을 반환한다.
      // null에 .then을 걸면 "Cannot read properties of null (reading 'then')"로
      // 부트스트랩(currentAccount/orgChart/getWorkspaceInfo 등)이 연쇄 크래시하므로 reject한다.
      // (LOGOUT/리다이렉트는 currentAccount의 token-null 가드가 담당 — 저수준 결합 회피)
      if (reqPromise == null) {
        reject(new Error('SESSION_EXPIRED'))
        return
      }
      reqPromise
        .then(async (response) => {
          // TOTP 시계 보정용 캘리브레이션(모든 REST 응답이 소스 — services/totp/timeSync).
          // Date 헤더가 CORS로 노출 안 되면 null → 내부에서 무시(무보정 폴백, throw 경로 없음).
          // 304는 제외(캐시 재검증 응답). headers는 테스트 mock에 없을 수 있어 옵셔널 체이닝.
          if (response.status !== 304 && typeof response.headers?.get === 'function') {
            updateServerTimeOffset(response.headers.get('date'))
          }
          const code = response.status
          const isSuccess = code >= 200 && code < 300
          let data
          if (response.json != null && code !== 204 && code !== 304) {
            if (isSuccess) {
              // 2xx 빈 body 등 파싱 실패는 빈 객체로 폴백(크래시 방지). try/catch는 비동기 reject뿐
              // 아니라 json()의 동기 throw(mock/비표준 fetch)까지 흡수한다.
              try {
                data = await response.json()
              } catch {
                data = {}
              }
            } else {
              // 오류 코드(4xx/5xx) 응답은 파싱 실패를 마스킹하지 않고 그대로 throw → 아래 .catch(reject)로 전파.
              data = await response.json()
            }
          } else {
            data = { message: response.message }
          }
          console.log(' data : ', data)

          const tryRescueOrLogout = async () => {
            let rescueAttempted = false
            if (retryFn) {
              const original = store.get('app.user.refreshToken')
              if (original) {
                rescueAttempted = true
                const rescued = await pollForRefreshTokenChange(original)
                if (rescued) {
                  try {
                    const retryResult = await processResponse(retryFn(), null)
                    return resolve(retryResult)
                  } catch (e) {
                    // fall through to logout
                  }
                }
              }
            }
            captureForcedLogout('rest-client.rescue-failed', {
              rescueAttempted,
              rescueDurationMs: rescueAttempted ? RESCUE_POLL_TIMEOUT_MS : 0,
              httpStatus: code,
              responseCode: data?.code,
              hasRefreshToken: !!store.get('app.user.refreshToken'),
            })
            reduxStore.dispatch({
              type: 'user/LOGOUT',
            })
            if (isErrorShown) {
              console.log(' ---- data.code : ', data.code)
            }
            isErrorShown = false
            return reject(new Error(JSON.stringify(data.code)))
          }

          if ((code >= 200 && code < 300) || code === 304) {
            // PR-F: KR 라이선스/계약 종료(890 계약만료 / 891 라이선스 비활성) 사용중 감지.
            // OX-4286: 822(로그인 차단)도 동일 UX(중앙 모달 + 로그아웃)로 편입 — jwt customFetch가
            // 아니라 여기(restClient)서 단독 처리해야 이중 반응(notification + '알 수 없는 에러')이 안 생긴다.
            // 토큰 rescue(509/403) 불가한 종료 상태 → licenseTerminated 세팅(layouts가 관찰해
            // 안내 팝업 + user/LOGOUT). 모듈 플래그 아닌 redux state로 중복판정(LOGOUT서 null
            // 리셋 → 재로그인 후 재감지 정상). getState는 index store.
            if (data.code === 890 || data.code === 891 || data.code === 822) {
              if (!reduxStore.getState()?.user?.licenseTerminated) {
                captureForcedLogout('rest-client.license-terminated', { code: data.code })
                reduxStore.dispatch({
                  type: 'user/SET_STATE',
                  payload: { licenseTerminated: data.code },
                })
              }
              return reject(new Error(JSON.stringify(data.code)))
            }
            if (data.code === 509 || data.code === 403) {
              return tryRescueOrLogout()
            }
            return resolve({
              origin: response,
              data,
            })
          }
          if (data.code !== 509 && data.code !== 403) {
            return resolve({
              origin: response,
              data,
            })
          }
          // HTTP error + body code 509/403 — 기존 코드는 resolve/reject 모두
          // 안 해 promise 가 영원히 pending 됐던 버그. rescue 경로로 통합.
          return tryRescueOrLogout()
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  self.get = (url, params, opts) => {
    let api = self.apiURL + url

    if (params != null) {
      const search = queryString.stringify(params)
      if (search != null) {
        api += `?${search}`
      }
    }

    const options = {
      method: 'GET',
      ...opts,
    }

    applyTokenForOptions(options, url)

    const send = () => self.fetch.call(window, api, options)

    return processResponse(send(), send)
  }

  self.post = (url, params, opts) => {
    const api = self.apiURL + url

    const options = {
      method: 'POST',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    options.headers['Content-Type'] = 'application/json'

    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      options.headers[HEADER_APP] = 'Web App'
      options.headers[HEADER_APP_VERSION] = packageInfo.version

      if (isSafboxWorkspace()) {
        options.headers['workspace-id'] = getWorkspaceId()
      }
    }

    applyTokenForOptions(options, url)

    const send = () => self.fetch.call(window, api, options)

    return processResponse(send(), send)
  }

  self.put = (url, params, opts) => {
    const api = self.apiURL + url

    const options = {
      method: 'PUT',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    options.headers['Content-Type'] = 'application/json'

    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      options.headers[HEADER_APP] = 'Web App'
      options.headers[HEADER_APP_VERSION] = packageInfo.version
    }

    if (isSafboxWorkspace()) {
      options.headers['workspace-id'] = getWorkspaceId()
    }

    applyTokenForOptions(options, url)

    const send = () => self.fetch.call(window, api, options)

    return processResponse(send(), send)
  }

  self.patch = (url, params, opts) => {
    const api = self.apiURL + url

    const options = {
      method: 'PATCH',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    options.headers['Content-Type'] = 'application/json'

    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      options.headers[HEADER_APP] = 'Web App'
      options.headers[HEADER_APP_VERSION] = packageInfo.version
    }

    if (isSafboxWorkspace()) {
      options.headers['workspace-id'] = getWorkspaceId()
    }

    applyTokenForOptions(options, url)

    const send = () => self.fetch.call(window, api, options)

    return processResponse(send(), send)
  }

  self.delete = (url, params, opts) => {
    const api = self.apiURL + url

    const options = {
      method: 'DELETE',
      body: JSON.stringify(params),
      ...opts,
    }

    if (options.headers == null) {
      options.headers = {}
    }

    options.headers['Content-Type'] = 'application/json'

    // auth 도메인 URL인지 확인 (restLandigURL 등 모든 auth 도메인 포함)
    const isAuthOfficeMailApp =
      self.apiURL.includes('auth.officemail') ||
      self.apiURL.includes('dev-auth.officemail') ||
      self.apiURL.includes('auth-jp.officemail') ||
      (url && url.includes('auth.officemail'))

    // auth 도메인이 아닌 경우에만 디바이스 및 버전 정보 추가
    if (!isAuthOfficeMailApp) {
      options.headers[HEADER_APP] = 'Web App'
      options.headers[HEADER_APP_VERSION] = packageInfo.version
    }

    if (isSafboxWorkspace()) {
      options.headers['workspace-id'] = getWorkspaceId()
    }

    applyTokenForOptions(options, url)

    const send = () => self.fetch.call(window, api, options)

    return processResponse(send(), send)
  }

  self.xhrGet = (url, params, onProgress, onBeforeSend, opts) => {
    // console.log(onProgress)
    const promise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      // xhr.withCredentials = true;

      xhr.addEventListener('progress', (e) => {
        if (onProgress != null) {
          onProgress(e, (e.loaded * 100) / e.total)
        }
      })
      xhr.addEventListener('load', (e) => {
        const { status } = e.target
        if (status >= 200 && status < 400) {
          let json = {}
          const body = xhr.response
          if (
            (xhr.responseType === '' || xhr.responseType === 'text') &&
            xhr.responseText != null &&
            xhr.responseText.charAt(0) === '{'
          ) {
            json = JSON.parse(xhr.responseText)
          }
          resolve({ status: xhr.status, body, json, data: json, event: e, xhr })
        } else if (status === 401) {
          updateToken()
            .then(() => {
              startReq()
            })
            .catch((err) => {
              reject(err)
            })
        } else {
          reject(e.target.response)
        }
      })

      xhr.addEventListener('error', (err) => reject(err))
      xhr.addEventListener('abort', (e) => reject(e))

      let api = self.apiURL + url

      if (params != null) {
        const search = queryString.stringify(params)
        if (search != null) {
          api += `?${search}`
        }
      }

      function startReq() {
        xhr.open('GET', api, true)

        // xhr.setRequestHeader('Content-Type', 'mutipart/form-data');

        applyTokenForXHR(xhr, url)

        if (opts != null && opts.headers != null) {
          Object.entries(opts.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value)
          })
        }

        if (onBeforeSend != null) {
          onBeforeSend(xhr)
        }

        xhr.send()
      }

      startReq()
    })

    return promise
  }

  self.xhrPost = (url, formData, onProgress, onBeforeSend, opts) => {
    console.log(' --- xhrPost ---')
    const promise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      // xhr.withCredentials = true;

      // opts가 undefined일 경우 빈 객체로 초기화
      if (opts == null) {
        opts = {}
      }
      if (opts.headers == null) {
        opts.headers = {}
      }

      if (onProgress != null) {
        xhr.upload.addEventListener('progress', (e) => onProgress(e, (e.loaded * 100) / e.total))
      }
      xhr.addEventListener('load', (e) => {
        const { status, response } = e.target
        if (status >= 200 && status < 400) {
          let json = {}
          const body = xhr.response
          if (
            (xhr.responseType === '' || xhr.responseType === 'text') &&
            xhr.responseText != null &&
            xhr.responseText.charAt(0) === '{'
          ) {
            json = JSON.parse(xhr.responseText)
          }

          if (JSON.parse(response)?.code === 401) {
            updateToken()
              .then(() => {
                startReq()
              })
              .catch((err) => {
                reject(err)
              })
          }
          resolve({ status: xhr.status, body, json, data: json, event: e, xhr })
        } else if (JSON.parse(response)?.code === 401) {
          updateToken()
            .then(() => {
              startReq()
            })
            .catch((err) => {
              reject(err)
            })
        } else {
          reject(e.target.response)
        }
      })

      xhr.addEventListener('error', (err) => reject(err))
      xhr.addEventListener('abort', (e) => reject(e))

      const api = self.apiURL + url

      function startReq() {
        xhr.open('POST', api, true)

        // xhr.setRequestHeader('Content-Type', 'mutipart/form-data');

        applyTokenForXHR(xhr, url)

        if (opts != null && opts.headers != null) {
          const headersSet = new Set()
          Object.entries(opts.headers).forEach(([key, value]) => {
            const lowerKey = key.toLowerCase()
            if (!headersSet.has(lowerKey)) {
              xhr.setRequestHeader(key, value)
              headersSet.add(lowerKey)
            }
          })
        }
        if (onBeforeSend != null) {
          onBeforeSend(xhr)
        }

        if (formData != null) {
          xhr.send(formData)
        } else {
          xhr.send()
        }
      }

      startReq()
    })

    return promise
  }

  self.xhrPut = (url, formData, onProgress, onBeforeSend, opts) => {
    // console.log(onProgress)
    const promise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      // xhr.withCredentials = true;

      // opts가 undefined일 경우 빈 객체로 초기화
      if (opts == null) {
        opts = {}
      }
      if (opts.headers == null) {
        opts.headers = {}
      }
      if (onProgress != null) {
        xhr.upload.addEventListener('progress', (e) => onProgress(e, (e.loaded * 100) / e.total))
      }
      xhr.addEventListener('load', (e) => {
        const { status, response } = e.target
        if (status >= 200 && status < 400) {
          let json = {}
          const body = xhr.response
          if (
            (xhr.responseType === '' || xhr.responseType === 'text') &&
            xhr.responseText != null &&
            xhr.responseText.charAt(0) === '{'
          ) {
            json = JSON.parse(xhr.responseText)
          }

          if (JSON.parse(response)?.code === 401) {
            updateToken()
              .then(() => {
                startReq()
              })
              .catch((err) => {
                reject(err)
              })
          }
          resolve({ status: xhr.status, body, json, data: json, event: e, xhr })
        } else if (status === 401) {
          updateToken()
            .then(() => {
              startReq()
            })
            .catch((err) => {
              reject(err)
            })
        } else {
          reject(e.target.response)
        }
      })

      xhr.addEventListener('error', (err) => reject(err))
      xhr.addEventListener('abort', (e) => reject(e))

      const api = self.apiURL + url

      function startReq() {
        xhr.open('PUT', api, true)

        // xhr.setRequestHeader('Content-Type', 'mutipart/form-data');

        applyTokenForXHR(xhr, url)

        if (opts != null && opts.headers != null) {
          Object.entries(opts.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value)
          })
        }

        if (onBeforeSend != null) {
          onBeforeSend(xhr)
        }

        if (formData != null) {
          xhr.send(formData)
        } else {
          xhr.send()
        }
      }

      startReq()
    })

    return promise
  }

  self.xhrPatch = (url, formData, onProgress, onBeforeSend, opts) => {
    // console.log(onProgress)
    const promise = new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      // xhr.withCredentials = true;

      // opts가 undefined일 경우 빈 객체로 초기화
      if (opts == null) {
        opts = {}
      }
      if (opts.headers == null) {
        opts.headers = {}
      }
      if (onProgress != null) {
        xhr.upload.addEventListener('progress', (e) => onProgress(e, (e.loaded * 100) / e.total))
      }
      xhr.addEventListener('load', (e) => {
        const { status } = e.target
        if (status >= 200 && status < 400) {
          let json = {}
          const body = xhr.response
          if (
            (xhr.responseType === '' || xhr.responseType === 'text') &&
            xhr.responseText != null &&
            xhr.responseText.charAt(0) === '{'
          ) {
            json = JSON.parse(xhr.responseText)
          }
          resolve({ status: xhr.status, body, json, data: json, event: e, xhr })
        } else if (status === 401) {
          updateToken()
            .then(() => {
              startReq()
            })
            .catch((err) => {
              reject(err)
            })
        } else {
          reject(e.target.response)
        }
      })

      xhr.addEventListener('error', (err) => reject(err))
      xhr.addEventListener('abort', (e) => reject(e))

      const api = self.apiURL + url

      function startReq() {
        xhr.open('PATCH', api, true)

        // xhr.setRequestHeader('Content-Type', 'mutipart/form-data');

        applyTokenForXHR(xhr, url)

        if (opts != null && opts.headers != null) {
          Object.entries(opts.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value)
          })
        }

        if (onBeforeSend != null) {
          onBeforeSend(xhr)
        }

        if (formData != null) {
          xhr.send(formData)
        } else {
          xhr.send()
        }
      }

      startReq()
    })

    return promise
  }
}

export default RestClient
